import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useIsOwnCandidate, useUpdateCandidate } from '../hooks'

const formSchema = z.object({
  comment: z.string().max(2000),
})

interface Props {
  onClose: () => void
  candidate: wowaudit.GuildCandidate
}

const CandidateCommentModal = ({ onClose, candidate }: Props) => {
  const isOwn = useIsOwnCandidate(candidate)
  const { mutate, isPending } = useUpdateCandidate(() => {
    toast.success('Comment saved')
    onClose()
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      comment: candidate.comment ?? '',
    },
  })

  const onSubmit = (values: z.infer<typeof formSchema>) =>
    mutate({
      characterId: candidate.characterReference.id,
      comment: values.comment,
    })

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Comment for {candidate.characterReference.name}
          </DialogTitle>
          <DialogDescription>
            {isOwn
              ? 'Only admins can see this comment.'
              : 'The candidate can see this comment.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            id='edit-candidate-comment-form'
          >
            <FormField
              control={form.control}
              name='comment'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comment</FormLabel>
                  <FormControl>
                    <Textarea placeholder='Comment' rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type='submit'
            form='edit-candidate-comment-form'
            loading={isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CandidateCommentModal
